import * as THREE from 'three';

import { COLORS } from '../game/constants';

/**
 * Shared material factories.
 *
 * Materials are created once and reused across every mesh that shares a surface
 * type, so a colour change is a one-line edit and draw calls stay low.
 *
 * The scene provides an environment map (see SceneManager), which is what makes
 * the metal and transparent-plastic surfaces read correctly.
 */

/** Brushed dark metal backing plate. */
export function createPlateMaterial(): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color: COLORS.plate,
    metalness: 0.78,
    roughness: 0.44,
    envMapIntensity: 0.55,
  });
}

/** Flat coloured annulus on the plate face ("colourful concentric sections"). */
export function createBandMaterial(color: number): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color,
    // A touch of self-illumination keeps the floor readable in the dark arcade
    // hall without washing out the contrast against the emissive walls.
    emissive: new THREE.Color(color),
    emissiveIntensity: 0.45,
    metalness: 0.3,
    roughness: 0.6,
    envMapIntensity: 0.4,
  });
}

/** A maze ring wall - moulded plastic with a slight neon bleed. */
export function createRingWallMaterial(color: number, emissiveIntensity: number): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color,
    emissive: new THREE.Color(color),
    emissiveIntensity,
    metalness: 0.15,
    roughness: 0.34,
    envMapIntensity: 0.8,
  });
}

/** The translucent blue outer containment ring. */
export function createOuterRingMaterial(): THREE.MeshPhysicalMaterial {
  return new THREE.MeshPhysicalMaterial({
    color: COLORS.outerRing,
    emissive: new THREE.Color(COLORS.outerRing),
    emissiveIntensity: 0.55,
    transparent: true,
    opacity: 0.46,
    roughness: 0.08,
    metalness: 0,
    clearcoat: 1,
    clearcoatRoughness: 0.05,
    envMapIntensity: 1.1,
    side: THREE.DoubleSide,
    depthWrite: false,
  });
}

/** The barely-there transparent front cover. */
export function createCoverMaterial(): THREE.MeshPhysicalMaterial {
  return new THREE.MeshPhysicalMaterial({
    color: COLORS.cover,
    transparent: true,
    opacity: 0.07,
    roughness: 0.03,
    metalness: 0,
    clearcoat: 1,
    clearcoatRoughness: 0.02,
    envMapIntensity: 1.4,
    side: THREE.DoubleSide,
    depthWrite: false,
  });
}

/** The polished metal ball. */
export function createBallMaterial(): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color: 0xffffff,
    metalness: 1,
    roughness: 0.08,
    envMapIntensity: 2.4,
  });
}

/** The raised hub at the centre of the board. */
export function createHubMaterial(): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color: COLORS.hub,
    emissive: new THREE.Color(COLORS.hub),
    emissiveIntensity: 0.35,
    metalness: 0.6,
    roughness: 0.3,
    envMapIntensity: 0.9,
  });
}

/** An unlit emissive ring - used for scoring boundaries and neon accents. */
export function createNeonMaterial(color: number, opacity = 0.95): THREE.MeshBasicMaterial {
  return new THREE.MeshBasicMaterial({
    color,
    transparent: true,
    opacity,
    toneMapped: false,
    side: THREE.DoubleSide,
    depthWrite: false,
  });
}

/** A brushed-metal surface: the scoring row's dividers and end walls. */
export function createMetalMaterial(color: number): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color,
    metalness: 0.95,
    roughness: 0.42,
    envMapIntensity: 1.1,
  });
}

/** A matte, unlit-ish surface: the scoring row's floor. */
export function createMatteMaterial(color: number): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color,
    metalness: 0.15,
    roughness: 0.75,
  });
}

/**
 * A pocket's back panel.
 *
 * Emissive, because the row's colours are how the player reads the values down
 * the row at a glance - the brightest panel is the jackpot. Presentation only:
 * nothing resolves a score from a colour.
 */
export function createSlotPanelMaterial(color: number): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color,
    emissive: new THREE.Color(color),
    emissiveIntensity: 0.7,
    metalness: 0.2,
    roughness: 0.45,
  });
}
